import React, { useState, useMemo, useCallback } from 'react';
import { Button } from '@/race-tracker/components/ui/button';
import { Input } from '@/race-tracker/components/ui/input';
import { Badge } from '@/race-tracker/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/race-tracker/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/race-tracker/components/ui/select';
import { Plus, Search, Pencil, Trash2, Phone, Mail, Download } from 'lucide-react';
import { useRepFirmManagers, useCreateManager, useUpdateManager, useDeleteManager, useCreateChangeLog } from '@/big-political-list/hooks/useBplData';
import { computeFieldChanges, MANAGER_FIELDS, MEDIA_TYPES } from '@/big-political-list/lib/stationUtils';
import ManagerFormModal from '@/big-political-list/components/managers/ManagerFormModal';

import { getBplUserName } from '@/big-political-list/lib/bplUser'
import BplQueryError from '@/big-political-list/components/shared/BplQueryError';
import { BplLogListSkeleton, BplUpdatingBanner } from '@/big-political-list/components/shared/BplSkeleton';

export default function RepFirmManagers({ canWrite = true, user, profile }) {
  const { data: managers = [], isLoading, isFetching, isError, error, refetch } = useRepFirmManagers();
  const showInitialSkeleton = isLoading && managers.length === 0;
  const createManager = useCreateManager();
  const updateManager = useUpdateManager();
  const deleteManager = useDeleteManager();
  const createLog = useCreateChangeLog();

  const [search, setSearch] = useState('');
  const [mediaType, setMediaType] = useState('Broadcast');
  const [formOpen, setFormOpen] = useState(false);
  const [editMgr, setEditMgr] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const visible = useMemo(() => {
    if (search) {
      const q = search.toLowerCase();
      return managers.filter(m =>
        (m.manager || '').toLowerCase().includes(q) ||
        (m.rep_firm || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
      );
    }
    if (mediaType === '_all') return managers;
    return managers.filter(m => (m.media_type || '') === mediaType);
  }, [managers, search, mediaType]);

  // Two-level grouping: media_type (MEDIA_TYPES order) → rep_firm (localeCompare)
  const grouped = useMemo(() => {
    const mediaOrder = (mt) => {
      const idx = MEDIA_TYPES.indexOf(mt);
      return idx === -1 ? MEDIA_TYPES.length : idx; // Unknown falls after all known
    };
    const mediaGroups = {};
    visible.forEach(m => {
      const mt = m.media_type || 'Unknown';
      if (!mediaGroups[mt]) mediaGroups[mt] = {};
      const firm = m.rep_firm || 'Unknown';
      if (!mediaGroups[mt][firm]) mediaGroups[mt][firm] = [];
      mediaGroups[mt][firm].push(m);
    });
    return Object.entries(mediaGroups)
      .sort(([a], [b]) => mediaOrder(a) - mediaOrder(b))
      .map(([mt, firms]) => [mt, Object.entries(firms).sort(([a], [b]) => a.localeCompare(b))]);
  }, [visible]);

  const userName = useMemo(() => getBplUserName(profile, user), [profile, user]);

  const handleSave = useCallback(async (formData) => {
    setSaving(true);
    if (editMgr?.id) {
      const changes = computeFieldChanges(editMgr, formData, MANAGER_FIELDS);
      await updateManager.mutateAsync({ id: editMgr.id, data: formData });
      if (changes.length > 0) {
        await createLog.mutateAsync({
          user: userName, action: 'updated', entity_type: 'RepFirmManager',
          record_label: `${formData.manager} — ${formData.rep_firm} (${formData.media_type})`,
          field_changes: changes,
        });
      }
    } else {
      await createManager.mutateAsync(formData);
      await createLog.mutateAsync({
        user: userName, action: 'created', entity_type: 'RepFirmManager',
        record_label: `${formData.manager} — ${formData.rep_firm} (${formData.media_type})`,
      });
    }
    setSaving(false);
    setFormOpen(false);
    setEditMgr(null);
  }, [editMgr, updateManager, createManager, createLog, userName]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteManager.mutateAsync(deleteTarget.id);
    await createLog.mutateAsync({
      user: userName, action: 'deleted', entity_type: 'RepFirmManager',
      record_label: `${deleteTarget.manager} — ${deleteTarget.rep_firm} (${deleteTarget.media_type})`,
    });
    setDeleteTarget(null);
  }, [deleteTarget, deleteManager, createLog, userName]);

  const handleExport = () => {
    const headers = MANAGER_FIELDS;
    const csv = [headers.join(','), ...visible.map(row => headers.map(h => `"${(row[h] || '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'rep_firm_managers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (isError && managers.length === 0) {
    return <BplQueryError label="managers" error={error} onRetry={refetch} />;
  }

  return (
    <div className="space-y-5">
      <BplUpdatingBanner
        message={
          showInitialSkeleton
            ? 'Loading managers…'
            : isFetching && managers.length > 0
              ? 'Refreshing managers…'
              : null
        }
      />
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 max-w-2xl">
          <Select value={mediaType} onValueChange={setMediaType}>
            <SelectTrigger className="w-48 h-9 text-xs bg-card shrink-0">
              <SelectValue placeholder="Media Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Media Types</SelectItem>
              {MEDIA_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search managers…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleExport}>
            <Download className="w-3.5 h-3.5 mr-1" /> Export
          </Button>
          <Button size="sm" disabled={!canWrite} className="h-8 text-xs bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50" onClick={() => { setEditMgr(null); setFormOpen(true); }}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Manager
          </Button>
        </div>
      </div>

      {showInitialSkeleton ? (
        <BplLogListSkeleton rows={8} />
      ) : (
      <div className="space-y-4">
        {grouped.map(([mt, firms]) => {
          const mediaCount = firms.reduce((s, [, mgrs]) => s + mgrs.length, 0);
          return (
          <div key={mt} className="space-y-3">
            <div className="flex items-baseline gap-2 pt-1">
              <h2 className="text-lg font-bold text-foreground">{mt}</h2>
              <span className="text-xs text-muted-foreground">{mediaCount} manager{mediaCount !== 1 ? 's' : ''}</span>
            </div>
            {firms.map(([firm, mgrs]) => (
              <div key={firm} className="rounded-lg border bg-card overflow-hidden">
                <div className="px-4 py-2.5 bg-navy/5 border-b flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs font-semibold">{firm}</Badge>
                  <span className="text-xs text-muted-foreground">{mgrs.length} manager{mgrs.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="divide-y">
                  {mgrs.map(m => (
                    <div key={m.id} className="px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                      <div className="flex-1 grid grid-cols-4 gap-4 items-center">
                        <div>
                          <p className="text-sm font-medium">{m.manager}</p>
                          {m.title && <p className="text-xs text-muted-foreground">{m.title}</p>}
                        </div>
                        <div>
                          {m.email ? (
                            <a href={`mailto:${m.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</a>
                          ) : <span className="text-sm text-muted-foreground">—</span>}
                        </div>
                        <div>
                          {m.office_phone ? (
                            <a href={`tel:${m.office_phone}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1"><Phone className="w-3 h-3" />{m.office_phone}</a>
                          ) : <span className="text-sm text-muted-foreground">—</span>}
                        </div>
                        <div>
                          {m.contact_mobile ? (
                            <a href={`tel:${m.contact_mobile}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1"><Phone className="w-3 h-3" />{m.contact_mobile}</a>
                          ) : <span className="text-sm text-muted-foreground">—</span>}
                        </div>
                      </div>
                      {canWrite && (
                        <div className="flex gap-1 ml-2">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditMgr(m); setFormOpen(true); }}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteTarget(m)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          );
        })}
        {grouped.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">No managers found.</div>
        )}
      </div>
      )}

      <ManagerFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditMgr(null); }} manager={editMgr} onSave={handleSave} saving={saving} />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Manager</AlertDialogTitle>
            <AlertDialogDescription>Delete {deleteTarget?.manager} from {deleteTarget?.rep_firm}?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
